import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'Como funciona a geração automática de laudos?',
    answer:
      'Ao realizar uma avaliação no RevaHub (TC6, TCPE, espirometria, CAT, mMRC, SGRQ, etc.), o sistema calcula automaticamente os valores preditos usando equações validadas internacionalmente (Enright & Sherrill 1998, Pereira et al. 2007, Neder et al. 1999), gera a interpretação clínica narrativa com classificação de severidade, monta gráficos profissionais com evolução temporal e exporta tudo em um PDF de alta qualidade — com a identidade visual da sua clínica e referências bibliográficas. O processo leva menos de 30 segundos.',
  },
  {
    question: 'Quais tipos de avaliação estão disponíveis?',
    answer:
      'O RevaHub oferece 13 tipos de avaliação: TC6 (Teste de Caminhada de 6 Minutos), TCPE/CPET (Teste Cardiopulmonar de Esforço), Espirometria, CAT (COPD Assessment Test), mMRC (Escala de Dispneia), PFSDQ-M (Status Funcional e Dispneia), SGRQ (Saint George), PImax/S-Index (Força Muscular Respiratória), HADS (Ansiedade e Depressão), STS 5x/30s/1min (Sentar-Levantar), LCADL (Atividades de Vida Diária) e MLHFQ (Qualidade de Vida na Insuficiência Cardíaca). Além disso, é possível criar avaliações multi-teste combinadas em um único relatório.',
  },
  {
    question: 'Os laudos são confiáveis para uso clínico e auditorias?',
    answer:
      'Sim. Todos os cálculos utilizam equações de referência publicadas em periódicos científicos indexados e amplamente aceitas pela comunidade acadêmica e pelo COFFITO. Cada laudo inclui as referências bibliográficas utilizadas, a metodologia de cálculo e a interpretação baseada em critérios clínicos padronizados. Os laudos são adequados para uso clínico, auditorias, relatórios para convênios e publicações científicas.',
  },
  {
    question: 'O RevaHub está em conformidade com o COFFITO e a LGPD?',
    answer:
      'Sim. O prontuário eletrônico do RevaHub foi desenvolvido para atender 100% dos 8 elementos obrigatórios da Resolução COFFITO 414/2012. Além disso, seguimos os princípios de Privacy by Design da LGPD (Lei 13.709/2018), com consentimento registrado, criptografia de dados sensíveis e controle de acesso baseado em perfis (RBAC).',
  },
  {
    question: 'Preciso instalar algum software?',
    answer:
      'Não. O RevaHub é uma plataforma 100% web (SaaS). Basta acessar pelo navegador em qualquer computador, tablet ou smartphone. Não há necessidade de instalação, configuração de servidores ou manutenção técnica da sua parte.',
  },
  {
    question: 'Como funciona a migração dos meus dados atuais?',
    answer:
      'Oferecemos migração assistida em todos os planos. Nossa equipe ajuda a importar dados de pacientes, históricos e informações financeiras de planilhas ou outros sistemas. No plano Enterprise, a migração é totalmente gerenciada por nós.',
  },
  {
    question: 'Posso testar antes de assinar?',
    answer:
      'Sim! Todos os planos incluem 14 dias de teste grátis, sem necessidade de cartão de crédito. Você pode explorar todas as funcionalidades do plano escolhido sem compromisso — incluindo todas as 13 avaliações com geração de laudos. Se não gostar, basta não continuar — sem multas ou burocracia.',
  },
  {
    question: 'O que acontece se eu precisar de mais profissionais no futuro?',
    answer:
      'Você pode fazer upgrade de plano a qualquer momento, de forma proporcional (pro-rata). Se sua clínica crescer, basta migrar para o plano Profissional ou Enterprise. A transição é instantânea e sem perda de dados.',
  },
  {
    question: 'Como funciona o suporte técnico?',
    answer:
      'No plano Essencial, oferecemos suporte por e-mail com tempo de resposta de até 24 horas. No plano Profissional, o suporte é prioritário com resposta em até 8 horas. No Enterprise, você conta com um gerente de conta dedicado e SLA de 4 horas para resolução.',
  },
  {
    question: 'Posso personalizar o sistema com a marca da minha clínica?',
    answer:
      'Sim! No plano Enterprise, você pode configurar logo, cores e nome personalizado para cada unidade (white-label). Seus pacientes acessam o portal com a identidade visual da sua clínica, e os laudos de avaliação são gerados com o cabeçalho personalizado da sua clínica.',
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-flex rounded-full bg-primary-50 border border-primary-200 px-3 py-1 text-sm font-medium text-primary-700 mb-4"
            >
              Dúvidas frequentes
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-4"
            >
              Tudo que você precisa saber
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-neutral-600"
            >
              Respondemos as perguntas mais comuns sobre o RevaHub. Não encontrou
              o que procura?{' '}
              <a
                href="/contato"
                className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-2"
              >
                Fale conosco
              </a>
              .
            </motion.p>
          </div>

          {/* Accordion */}
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="rounded-xl border border-neutral-200 bg-neutral-50/50 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-neutral-50 transition-colors"
                  aria-expanded={openIndex === index}
                >
                  <span className="text-base font-medium text-neutral-900 pr-4">
                    {faq.question}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`shrink-0 text-neutral-400 transition-transform duration-200 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                      <div className="px-6 pb-5">
                        <p className="text-neutral-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
